/**
 * The Pokémon sheet (slice 11g; ui-style-guide "The Pokémon sheet"): one native <dialog> in the
 * Book's sheet style for one creature, opened from a pick card, a Lineup row (with that slot as
 * context) or the roster strip. Top to bottom: sprite, name, types, where it is listed and where
 * it plays; its max HP at level `pvp.level` as a bar with the reference's tick and the stamina line
 * under it ("lasts 6 lost battles before he tires · most last about 6", slice 10i); its stats as
 * bars, with a tick at each listing screen that reads the stat; all nine positions with what each
 * does, the stat it reads, the value after position
 * fit, the grade, and the other battles there; with a context slot, the arithmetic in a sentence
 * and the best alternative on the roster. Slice 14's Pokémon page extends this sheet.
 *
 * `sheetModel` is pure: every number is the sim's (`statsAtLevel` through `combatantOf`,
 * `listedPositions` through `positionsOf`, `slotGrade`, `slotThresholds` for the bars' words, and
 * `levelStat` over `positionFit.listing`), never worked out here.
 */
import {
  OFFENSE_SLOTS,
  DEFENSE_SLOTS,
  SLOT_POSITION,
  balance,
  combatantOf,
  lineOf,
  lostBattlesBeforeTired,
  referenceMaxHp,
  referenceStamina,
  sideStat,
  slotGrade,
  statsAtLevel,
  statsOf,
  type Balance,
  type Combatant,
  type Creature,
  type Grade,
  type GradeStat,
  type Position,
  type RosterSlot,
  type TypeName,
} from '@tdmons/sim';
import { typeChip } from '../ui/matchupBoard';
import {
  GRADE_RANK,
  POSITION_ORDER,
  SHORT_STAT_WORDS,
  battle,
  gradeBar,
  levelStat,
  secondaryBattles,
  sideOf,
  statWords,
  type Battle,
  type Side,
} from './positions';
import { combatant, positionsOf, slotOf, type PackIndex, type RosterState } from './state';

/** The bars' full length: the games' highest base stat (the style guide's 255 scale). */
export const BAR_MAX_BASE = 255;

/**
 * A bar's stat: HP first (slice 10i, the stamina line's bar), the five battle stats, and each
 * averaged pair a battle reads (avg(Atk, Def), the block strength, with the shipped map), drawn
 * derived.
 */
export type BarStat = 'hp' | GradeStat;

const STAT_ORDER = ['atk', 'def', 'spa', 'spd', 'spe'] as const;
const KINDS = ['block', 'coverage', 'tackle', 'contested', 'throw'] as const;

/**
 * The bars in order: the five stats, each averaged pair balance.json `battles` reads (the kick
 * aside: the kicker is not a slot) placed after the later of its two stats (slice 10g).
 */
function barOrder(b: Balance): BarStat[] {
  const pairs = new Set<GradeStat>();
  for (const k of KINDS) {
    for (const side of ['offense', 'defense'] as const) {
      const key = sideStat(k, side, b);
      if (statsOf(key).length === 2) pairs.add(key);
    }
  }
  const last = (key: GradeStat) => Math.max(...statsOf(key).map((s) => STAT_ORDER.indexOf(s)));
  return ['hp', ...STAT_ORDER.flatMap((s, i) => [s, ...[...pairs].filter((p) => last(p) === i)])];
}

/** A bar's label: HP, a stat's short word, a pair's words. */
const barLabel = (s: BarStat) =>
  s === 'hp'
    ? 'HP'
    : statsOf(s).length === 1
      ? SHORT_STAT_WORDS[s as (typeof STAT_ORDER)[number]]
      : statWords(s);

/** The bars a grade stat lights: a pair is read from both its stats. */
const lights = (s: GradeStat): readonly GradeStat[] =>
  statsOf(s).length === 1 ? [s] : [...statsOf(s), s];

export interface Tick {
  /** The listing screen's minimum at level `pvp.level`; on the HP bar, the reference's max HP. */
  at: number;
  /** The positions the screen lists: "WR · CB"; on the HP bar, "most Pokémon". */
  positions: string;
}

/** The stamina line under the HP bar (slice 10i): his count, and the reference's for scale. */
export interface Stamina {
  lasts: number;
  reference: number;
}

export interface StatBar {
  stat: BarStat;
  label: string;
  value: number;
  max: number;
  ticks: Tick[];
  /** avg(Atk, Def): drawn muted, under Def. */
  derived: boolean;
  /** The context slot reads it. */
  lit: boolean;
}

export interface PositionRow {
  position: Position;
  /** The slot the row is graded at: the position's best. */
  slot: RosterSlot;
  /** Its board line's battle: "throws · Sp. Atk". */
  main: Battle;
  /** After position fit, at level `pvp.level`. */
  value: number;
  grade: Grade;
  listed: boolean;
  fit: number;
  /** The other battles there: "contested catch · Sp. Def". */
  secondary: Battle[];
}

export interface SheetContext {
  side: Side;
  slot: RosterSlot;
}

export interface SheetModel {
  id: string;
  name: string;
  types: readonly TypeName[];
  listed: Position[];
  /** The slots it holds in your lineup, offense first. */
  lineup: RosterSlot[];
  bars: StatBar[];
  stamina: Stamina;
  rows: PositionRow[];
  context?: SheetContext;
  /** With a context: the arithmetic in one sentence. */
  footer?: string;
  /** With a context: a better choice on the roster for that slot, if there is one. */
  alternative?: string;
}

const article = (g: Grade) => (g === 'A' ? 'an A' : `a ${g}`);
const statName = (s: GradeStat) => statWords(s);

function barValue(c: Combatant, s: BarStat): number {
  if (s === 'hp') return c.stats.hp;
  const stats = statsOf(s);
  return stats.reduce((sum, x) => sum + c.stats[x], 0) / stats.length;
}

/** The HP bar's full length: a 255-base HP at level `pvp.level`. */
function maxHpBar(b: Balance): number {
  const x = BAR_MAX_BASE;
  return statsAtLevel({ hp: x, atk: x, def: x, spa: x, spd: x, spe: x }, b.pvp.level).hp;
}

function bars(c: Combatant, context: SheetContext | undefined, b: Balance): StatBar[] {
  const pairs = b.positionFit.listing.pairs as readonly {
    positions: readonly string[];
    min: Partial<Record<BarStat, number>>;
  }[];
  const lit = new Set<BarStat>(context ? lights(slotGrade(c, context.slot, b).stat) : []);
  return barOrder(b).map((stat) =>
    stat === 'hp'
      ? {
          // Slice 10i: max HP at level `pvp.level`, the reference's max as the one tick.
          stat,
          label: barLabel(stat),
          value: c.stats.hp,
          max: maxHpBar(b),
          ticks: [{ at: referenceMaxHp(b), positions: 'most Pokémon' }],
          derived: false,
          lit: false,
        }
      : {
          stat,
          label: barLabel(stat),
          value: Math.floor(barValue(c, stat)),
          max: levelStat(BAR_MAX_BASE, b),
          ticks: pairs.flatMap((p) => {
            const min = p.min[stat];
            return min === undefined
              ? []
              : [{ at: levelStat(min, b), positions: p.positions.join(' · ') }];
          }),
          derived: statsOf(stat).length === 2,
          lit: lit.has(stat),
        },
  );
}

function rows(c: Combatant, b: Balance): PositionRow[] {
  const slots: readonly RosterSlot[] = [...OFFENSE_SLOTS, ...DEFENSE_SLOTS];
  return POSITION_ORDER.map((position) => {
    let best: { slot: RosterSlot; g: ReturnType<typeof slotGrade> } | undefined;
    for (const slot of slots) {
      if (SLOT_POSITION[slot] !== position) continue;
      const g = slotGrade(c, slot, b);
      if (!best || g.value > best.g.value) best = { slot, g };
    }
    const { slot, g } = best as { slot: RosterSlot; g: ReturnType<typeof slotGrade> };
    return {
      position,
      slot,
      main: battle(lineOf(slot).kind as Battle['kind'], sideOf(slot), b),
      value: Math.floor(g.value),
      grade: g.grade,
      listed: g.fit === 1,
      fit: g.fit,
      secondary: secondaryBattles(slot, undefined, b),
    };
  });
}

/**
 * "At TE he isn't listed, so he plays at x0.9: Speed 140 → 126, still an A; A here is 120+ at
 * level 50." The unpenalised value is `slotGrade` with the position added to his listing; the bar
 * is the slot's board-line side's (slice 10i).
 */
function footerOf(c: Combatant, slot: RosterSlot, b: Balance): string {
  const g = slotGrade(c, slot, b);
  const position = SLOT_POSITION[slot];
  const level = b.pvp.level;
  const stat = statName(g.stat);
  const value = Math.floor(g.value);
  const bar = `${gradeBar(g.grade, slot, b)} at level ${level}`;
  if (g.fit === 1)
    return `At ${position} he's listed: ${stat} ${value}, ${article(g.grade)}; ${bar}.`;
  const full = slotGrade({ ...c, positions: [...(c.positions ?? []), position] }, slot, b);
  const grade =
    full.grade === g.grade
      ? `still ${article(g.grade)}`
      : `down from ${article(full.grade)} to ${article(g.grade)}`;
  return `At ${position} he isn't listed, so he plays at x${g.fit}: ${stat} ${Math.floor(full.value)} → ${value}, ${grade}; ${bar}.`;
}

/**
 * The best Pokémon on the roster for `slot` by value who is not starting on that side, named
 * only when it beats `c` there: (a) a higher grade, or (b) `c` plays it out of position and the
 * other is listed there at as high a value or higher. "would be an A here", "… too" on a tie.
 */
function alternativeOf(
  c: Combatant,
  context: SheetContext,
  state: RosterState,
  pack: PackIndex,
  b: Balance,
): string | undefined {
  const g = slotGrade(c, context.slot, b);
  const chart = state[context.side] as Partial<Record<RosterSlot, number>>;
  const starting = new Set(Object.values(chart));
  let best: { name: string; g: ReturnType<typeof slotGrade> } | undefined;
  state.picks.forEach((p, i) => {
    if (starting.has(i) || p.id === c.creatureId) return;
    const other = combatant(state, pack, i, b);
    const o = slotGrade(other, context.slot, b);
    const better =
      GRADE_RANK[o.grade] > GRADE_RANK[g.grade] || (g.fit < 1 && o.fit === 1 && o.value >= g.value);
    if (better && (!best || o.value > best.g.value)) best = { name: other.name, g: o };
  });
  if (!best) return undefined;
  const o = best.g;
  const listedHere = g.fit < 1 && o.fit === 1 ? `, listed at ${SLOT_POSITION[context.slot]}` : '';
  const too = o.grade === g.grade ? ' too' : '';
  return `${best.name}, ${statName(o.stat)} ${Math.floor(o.value)}${listedHere}, would be ${article(o.grade)} here${too}.`;
}

/** Everything the sheet shows for `creature`; with `context`, the footer for that slot. */
export function sheetModel(
  creature: Creature,
  state: RosterState,
  pack: PackIndex,
  context?: SheetContext,
  b: Balance = balance,
): SheetModel {
  const listed = positionsOf(creature, b);
  const c = combatantOf(creature, [], listed, b);
  const at = state.picks.findIndex((p) => p.id === creature.id);
  const lineup =
    at < 0
      ? []
      : [slotOf(state, 'offense', at), slotOf(state, 'defense', at)].filter(
          (s): s is RosterSlot => s !== undefined,
        );
  const alternative = context ? alternativeOf(c, context, state, pack, b) : undefined;
  return {
    id: creature.id,
    name: creature.name,
    types: creature.types,
    listed: POSITION_ORDER.filter((p) => listed.includes(p)),
    lineup,
    bars: bars(c, context, b),
    stamina: { lasts: lostBattlesBeforeTired(c.stats.hp, b), reference: referenceStamina(b) },
    rows: rows(c, b),
    ...(context ? { context, footer: footerOf(c, context.slot, b) } : {}),
    ...(alternative ? { alternative } : {}),
  };
}

// The dialog ------------------------------------------------------------------------------------

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

/** "TE and S", "QB, WR1 and S". */
function list(words: readonly string[]): string {
  if (words.length < 2) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`;
}

/**
 * The sheet as a native <dialog> (`#mon-sheet`): Escape, the backdrop and the close button close
 * it, and focus goes back to the control that opened it.
 */
export class PokemonSheet {
  private opener: HTMLElement | null = null;
  private readonly title: HTMLElement;
  private readonly body: HTMLElement;

  constructor(
    private readonly dialog: HTMLDialogElement,
    /** Frame 0 of the front sheet, the pick grid's loader. */
    private readonly sprite: (id: string) => HTMLElement,
  ) {
    const part = (sel: string) => {
      const e = dialog.querySelector<HTMLElement>(sel);
      if (!e) throw new Error(`the Pokémon sheet has no ${sel}`);
      return e;
    };
    this.title = part('#mon-sheet-title');
    this.body = part('.mon-sheet-body');
    part('.book-close').addEventListener('click', () => this.close());
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });
    dialog.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      this.close();
    });
    // A click on the dialog itself, not its contents, is on the backdrop.
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.close();
    });
  }

  get isOpen(): boolean {
    return this.dialog.open || this.dialog.hasAttribute('open');
  }

  open(model: SheetModel, opener: HTMLElement | null): void {
    this.opener = opener;
    this.render(model);
    if (!this.dialog.open) this.dialog.showModal?.();
    if (!this.dialog.open) this.dialog.setAttribute('open', '');
    this.body.scrollTop = 0;
  }

  close(): void {
    if (!this.isOpen) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    this.dialog.removeAttribute('open');
    const opener = this.opener;
    this.opener = null;
    if (opener?.isConnected) opener.focus();
  }

  private render(m: SheetModel): void {
    this.title.textContent = m.name;
    const head = el('div', 'mon-head');
    const chips = el('span', 'roster-chips');
    chips.append(...m.types.map(typeChip));
    const where = el('p', 'mon-where');
    where.textContent = `Listed at ${m.listed.join(' · ')}`;
    if (m.lineup.length > 0) {
      where.append(el('br'), `in your lineup at ${list(m.lineup)}`);
    }
    const id = el('div', 'mon-id');
    id.append(chips, where);
    head.append(this.sprite(m.id), id);

    const stats = el('div', 'mon-stats');
    stats.setAttribute('role', 'list');
    stats.setAttribute('aria-label', 'Stats at level 50');
    for (const bar of m.bars) {
      stats.append(this.bar(bar, m.context !== undefined));
      // Slice 10i: the stamina line under the HP bar, the reference's count beside his.
      if (bar.stat === 'hp') {
        stats.append(
          el(
            'p',
            'mon-stamina',
            `lasts ${m.stamina.lasts} lost battles before he tires · most last about ${m.stamina.reference}`,
          ),
        );
      }
    }
    const note = el(
      'p',
      'mon-note',
      'A tick is a listing screen: reach every tick of a pair to list its positions.',
    );

    const table = el('div', 'mon-table');
    table.setAttribute('role', 'table');
    table.setAttribute('aria-label', 'Every position');
    for (const r of m.rows) table.append(this.row(r, m.context));

    const parts: HTMLElement[] = [head, stats, note, table];
    if (m.footer) {
      const foot = el('div', 'mon-foot');
      foot.append(el('p', 'mon-footer', m.footer));
      if (m.alternative) foot.append(el('p', 'mon-alt', m.alternative));
      parts.push(foot);
    }
    this.body.replaceChildren(...parts);
  }

  private bar(b: StatBar, dim: boolean): HTMLElement {
    const row = el(
      'div',
      `mon-bar${b.derived ? ' derived' : ''}${b.lit ? ' lit' : ''}${dim && !b.lit && b.stat !== 'hp' ? ' dim' : ''}`,
    );
    row.setAttribute('role', 'listitem');
    row.dataset.stat = b.stat;
    const track = el('span', 'mon-track');
    const fill = el('i', 'mon-fill');
    fill.style.width = `${Math.min(100, (b.value / b.max) * 100)}%`;
    track.append(fill);
    for (const t of b.ticks) {
      const tick = el('span', 'mon-tick');
      tick.style.left = `${Math.min(100, (t.at / b.max) * 100)}%`;
      tick.dataset.at = String(t.at);
      tick.title = `${t.positions}: ${t.at}+`;
      track.append(tick);
    }
    row.append(
      el('span', 'mon-bar-label', b.label),
      track,
      el('span', 'mon-bar-value', String(b.value)),
    );
    const screens = b.ticks.map((t) => `${t.positions} at ${t.at}`).join(', ');
    row.setAttribute(
      'aria-label',
      `${b.label} ${b.value}${screens ? `; screens: ${screens}` : ''}`,
    );
    return row;
  }

  private row(r: PositionRow, context: SheetContext | undefined): HTMLElement {
    const here = context !== undefined && SLOT_POSITION[context.slot] === r.position;
    const row = el('div', `mon-row${here ? ' here' : ''}`);
    row.setAttribute('role', 'row');
    row.dataset.position = r.position;
    const fit = r.listed
      ? el('span', 'mon-fit listed', '✓')
      : el('span', 'mon-fit out', `x${r.fit}`);
    fit.setAttribute('aria-label', r.listed ? 'listed' : `not listed, times ${r.fit}`);
    const grade = el('span', 'grade-box', r.grade);
    grade.dataset.grade = r.grade;
    const cell = (child: HTMLElement | string, cls: string) => {
      const c = el('span', cls);
      c.setAttribute('role', 'cell');
      c.append(child);
      return c;
    };
    row.append(
      cell(r.position, 'mon-pos'),
      cell(`${r.main.words} · ${r.main.stat}`, 'mon-does'),
      cell(String(r.value), 'mon-value'),
      cell(grade, 'mon-grade'),
      cell(fit, 'mon-fit-cell'),
    );
    if (r.secondary.length > 0) {
      row.append(
        el('span', 'mon-also', r.secondary.map((s) => `${s.words} · ${s.stat}`).join(' / ')),
      );
    }
    return row;
  }
}
